import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInView, animate } from "framer-motion";
import { ArrowUpRight, Quote } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { PILLARS, WHY, PROCESS, STATS, TESTIMONIALS, IMAGES } from "../data/content";

export const Pillars = () => (
  <section data-testid="pillars-section" className="relative bg-dg-light bg-grid-light py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead
        light
        eyebrow="One Hub. Three Powers."
        title="Everything Your Business Needs to Go Digital."
        sub="DG Hubs combines creativity, technology and digital marketing to help businesses establish a strong online presence, reach the right audience and convert attention into growth."
      />
      <div className="grid gap-6 md:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.12}>
            <div
              data-testid={`pillar-${p.title.toLowerCase()}`}
              className="group relative h-full overflow-hidden rounded-3xl border border-dg-blue/10 bg-white p-8 sm:p-10 shadow-xl shadow-blue-900/5 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-dg-blue/15"
            >
              <span className="font-mono text-[11px] tracking-[0.3em] text-dg-blue/50">0{i + 1}</span>
              <div className="mt-6 grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-dg-blue to-dg-cyan text-white shadow-lg shadow-dg-blue/25 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                <p.icon size={24} />
              </div>
              <h3 className="font-display mt-7 text-2xl font-extrabold tracking-[0.12em] text-dg-navy">{p.title}</h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">{p.desc}</p>
              <span className="absolute -bottom-8 -right-8 h-28 w-28 rounded-full bg-dg-cyan/10 transition-transform duration-500 group-hover:scale-150" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const WhyUs = () => (
  <section data-testid="why-section" className="relative bg-dg-navy py-24 sm:py-32 noise">
    <div className="absolute top-0 right-0 h-72 w-72 rounded-full bg-dg-cyan/8 blur-[120px]" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead
        eyebrow="The DG Hubs Difference"
        title="Why Businesses Choose DG Hubs"
        sub="Six reasons ambitious businesses trust one hub with their entire digital ecosystem."
      />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.08}>
            <div
              data-testid={`why-card-${i}`}
              className="group h-full rounded-2xl border border-white/8 bg-white/[0.03] p-7 transition-all duration-500 hover:border-dg-cyan/40 hover:bg-white/[0.06] hover:shadow-[0_0_30px_rgba(0,184,212,0.12)]"
            >
              <span className="font-mono text-xs text-dg-cyan/60">0{i + 1}</span>
              <h3 className="font-display mt-4 text-lg sm:text-xl font-bold text-white">{w.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Process = () => (
  <section data-testid="process-section" className="relative bg-dg-light bg-grid-light py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-14 lg:grid-cols-[1fr_1.3fr]">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <SectionHead
            light
            eyebrow="How We Work"
            title="From Idea to Growth — We Make It Digital."
            sub="A proven six-step growth engine, refined across 100+ projects."
          />
          <Reveal delay={0.2}>
            <div className="relative overflow-hidden rounded-3xl shadow-2xl shadow-blue-900/15">
              <img src={IMAGES.brainstorm} alt="Digital growth strategy session at DG Hubs" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-dg-navy/70 to-transparent" />
              <p className="absolute bottom-5 left-5 font-mono text-[11px] uppercase tracking-[0.25em] text-white/90">Strategy → Execution → Scale</p>
            </div>
          </Reveal>
        </div>
        <div className="relative">
          <span className="absolute left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-dg-cyan via-dg-blue to-transparent" />
          <div className="space-y-10">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div data-testid={`process-step-${p.step}`} className="group relative flex gap-7 pl-1">
                  <span className="relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-2xl border border-dg-blue/15 bg-white font-mono text-sm font-bold text-dg-blue shadow-lg shadow-blue-900/5 transition-all duration-500 group-hover:border-dg-cyan group-hover:bg-dg-navy group-hover:text-dg-cyan">
                    {p.step}
                  </span>
                  <div className="pt-1.5">
                    <h3 className="font-display text-xl font-extrabold tracking-[0.1em] text-dg-navy">{p.title}</h3>
                    <p className="mt-1.5 text-sm sm:text-base text-slate-600">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref} className="font-display text-5xl sm:text-6xl font-extrabold text-white tabular-nums">
      {display}
      <span className="text-grad">{suffix}</span>
    </span>
  );
};

export const Stats = () => (
  <section data-testid="stats-section" className="relative overflow-hidden bg-dg-navy py-20 sm:py-24 noise">
    <div className="absolute inset-0 bg-grid-dark" />
    <div className="absolute -top-24 left-1/4 h-64 w-64 rounded-full bg-dg-blue/25 blur-[120px]" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid grid-cols-2 gap-y-12 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1} className="text-center">
            <div data-testid={`stat-${i}`}>
              <Counter value={s.value} suffix={s.suffix} />
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10">
        <p data-testid="stats-disclaimer" className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
          Indicative figures — will be updated with live company data
        </p>
      </Reveal>
    </div>
  </section>
);

export const Testimonials = () => (
  <section data-testid="testimonials-section" className="relative bg-dg-light bg-grid-light py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead light eyebrow="Client Love" title="What Our Clients Say" sub="Real words from businesses growing with DG Hubs. (Sample testimonials — client quotes being added.)" />
      <div className="grid gap-6 md:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.09}>
            <div
              data-testid={`testimonial-${i}`}
              className="flex h-full flex-col rounded-3xl border border-dg-blue/10 bg-white p-8 shadow-xl shadow-blue-900/5 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-dg-blue/10"
            >
              <Quote size={26} className="text-dg-cyan" />
              <p className="mt-5 flex-1 text-sm sm:text-base leading-relaxed text-slate-700">"{t.quote}"</p>
              <div className="mt-7 flex items-center gap-4 border-t border-slate-100 pt-5">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-dg-blue to-dg-cyan font-display text-sm font-bold text-white">
                  {t.name.split(" ").map((n) => n[0]).join("")}
                </span>
                <div>
                  <p className="font-display text-sm font-bold text-dg-navy">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.company} · {t.industry}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const HomeCtaStrip = () => {
  const navigate = useNavigate();
  return (
    <section className="border-y border-cyan-500/10 bg-dg-navy py-10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-5 px-5 sm:px-8 sm:flex-row">
        <p className="font-display text-lg sm:text-xl font-bold text-white text-center sm:text-left">
          Have a project in mind? <span className="text-grad">Let's talk growth.</span>
        </p>
        <button
          data-testid="strip-talk-expert-btn"
          onClick={() => navigate("/contact")}
          className="inline-flex items-center gap-2 rounded-full border border-dg-cyan/50 px-6 py-3 text-sm font-bold text-dg-cyan transition-all duration-300 hover:bg-dg-cyan hover:text-dg-navy"
        >
          Talk to Our Expert <ArrowUpRight size={15} />
        </button>
      </div>
    </section>
  );
};
